// lib/koi-pond-v2/shaders/waterSurface.ts
//
// Phase 1 water surface preview: vertex displacement from 3 trochoidal-
// ish sin waves at different frequencies and directions, fragment does
// a fresnel-driven mix from the deep water palette (at normal incidence)
// to the warm surface highlight (at glancing angles). No caustics, no
// refraction — Phase 1 isolates surface behavior.

import { ShaderMaterial, Vector2 } from 'three';
import { paletteVec3 } from '../palette';
import { SAFE_ZONE_COMPLEMENT } from '../constants/safeZone';

export const WATER_SURFACE_VERT = `
  varying vec2 vUv;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPos;
  uniform float uTime;
  uniform float uWind;

  // Three sin waves at different frequencies/directions
  void waveContrib(vec3 pos, out float height, out vec3 normal) {
    height = 0.0;
    normal = vec3(0.0, 1.0, 0.0);
    // Wave 1
    {
      vec2 dir = normalize(vec2(1.0, 0.4));
      float k = 1.4;
      float w = 1.1;
      float phase = dot(dir, pos.xz) * k - uTime * w;
      float w1 = 0.08 * (1.0 + 0.08 * uWind);
      height += sin(phase) * w1;
      normal.x -= dir.x * cos(phase) * w1 * k;
      normal.z -= dir.y * cos(phase) * w1 * k;
    }
    // Wave 2
    {
      vec2 dir = normalize(vec2(-0.3, 1.0));
      float k = 2.3;
      float w = 1.7;
      float phase = dot(dir, pos.xz) * k - uTime * w;
      float w2 = 0.05 * (1.0 + 0.08 * uWind);
      height += sin(phase) * w2;
      normal.x -= dir.x * cos(phase) * w2 * k;
      normal.z -= dir.y * cos(phase) * w2 * k;
    }
    // Wave 3 (small ripple)
    {
      vec2 dir = normalize(vec2(0.6, -0.8));
      float k = 4.7;
      float w = 2.6;
      float phase = dot(dir, pos.xz) * k - uTime * w;
      float w3 = 0.02 * (1.0 + 0.08 * uWind);
      height += sin(phase) * w3;
      normal.x -= dir.x * cos(phase) * w3 * k;
      normal.z -= dir.y * cos(phase) * w3 * k;
    }
    normal = normalize(normal);
  }

  void main() {
    vUv = uv;
    float h;
    vec3 n;
    waveContrib(position, h, n);
    vec3 displaced = position + vec3(0.0, h, 0.0);
    vec4 worldPos = modelMatrix * vec4(displaced, 1.0);
    vWorldPos = worldPos.xyz;
    vWorldNormal = normalize(mat3(modelMatrix) * n);
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
`;

export const WATER_SURFACE_FRAG = `
  precision highp float;
  varying vec2 vUv;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPos;
  uniform vec2 uResolution;

  void main() {
    vec3 N = normalize(vWorldNormal);
    vec3 V = normalize(cameraPosition - vWorldPos);
    float NdotV = max(0.0, dot(N, V));
    float fresnel = pow(1.0 - NdotV, 4.0);

    vec3 deep = ${paletteVec3('deepestWater')};
    vec3 mid = ${paletteVec3('midWaterLight')};
    vec3 highlight = ${paletteVec3('surfaceHighlight')};
    vec3 sky = ${paletteVec3('lightShaftGlow')};

    // Normal-incidence: deep blue-brown; glancing: warm sky
    vec3 baseColor = mix(deep, mid, NdotV * 0.4);
    vec3 finalColor = mix(baseColor, sky, fresnel);
    // A touch of surface-highlight scatter for the in-between
    finalColor = mix(finalColor, highlight, fresnel * 0.3 * (1.0 - NdotV));

    // Phase 5 Task 3 — zone-graduated opacity. Water above the 28%
    // safe-zone line (top of screen) stays opaque (matches Phase 4
    // close behavior); water below smoothly fades to 30% alpha so
    // koi swimming under the surface become visible without raising
    // the safe-zone bg L distribution that the hero text reads against.
    // The safe-zone constraint becomes a first-class scene property
    // rather than an HTML-overlay post-composition fix.
    //
    // gl_FragCoord.y origin is BOTTOM-left in WebGL — so high y
    // values are at the TOP of screen, opposite of CSS convention.
    // safeZoneEdgeNorm = 1 - SAFE_ZONE_TOP_FRACTION = SAFE_ZONE_COMPLEMENT.
    // The 28% safe-zone line measured from CSS-top corresponds to 72%
    // of viewport height measured from WebGL bottom. Constant injected
    // at JS evaluation time via template-string substitution — single
    // source of truth lives at lib/koi-pond-v2/constants/safeZone.ts.
    float screenYNorm = gl_FragCoord.y / uResolution.y;
    float safeZoneEdgeNorm = ${SAFE_ZONE_COMPLEMENT};
    float fadeBandHalfWidth = 0.04; // ~3% screen height transition
    float zoneAlpha = mix(
      0.3,  // alpha below safe zone (koi visible through translucent water)
      1.0,  // alpha above safe zone (opaque, current Phase 4 behavior preserved)
      smoothstep(
        safeZoneEdgeNorm - fadeBandHalfWidth,
        safeZoneEdgeNorm + fadeBandHalfWidth,
        screenYNorm
      )
    );

    gl_FragColor = vec4(finalColor, zoneAlpha);
  }
`;

export function makeWaterSurfaceMaterial(): {
  material: ShaderMaterial;
  uniforms: {
    uTime: { value: number };
    uWind: { value: number };
    uResolution: { value: Vector2 };
  };
} {
  // Initialize uResolution to drawing-buffer dims if a window is
  // available (client), else (1,1) sentinel for SSR. Either way the
  // scene's mount-time set will overwrite, but this hardens the
  // factory against a future caller that forgets the explicit set —
  // safe-zone alpha would just be approximately right on client mount
  // instead of producing the unbounded-screenYNorm degenerate case.
  const initialW =
    typeof window !== 'undefined' ? window.innerWidth * (window.devicePixelRatio || 1) : 1;
  const initialH =
    typeof window !== 'undefined' ? window.innerHeight * (window.devicePixelRatio || 1) : 1;
  const uniforms = {
    uTime: { value: 0 },
    uWind: { value: 0 },
    uResolution: { value: new Vector2(initialW, initialH) },
  };
  const material = new ShaderMaterial({
    vertexShader: WATER_SURFACE_VERT,
    fragmentShader: WATER_SURFACE_FRAG,
    uniforms,
    transparent: true,
    // Phase 5 Task 5.3d — depthWrite kept ENABLED on the water surface
    // (overrides the usual "transparent surface ⇒ depthWrite:false"
    // pattern). Render-order diagnostic confirmed that depthWrite:false
    // let godrays (z=-8, far back, AdditiveBlending) bleed into the
    // above-water "no plane" region at top of frame: clear/far depth
    // post-water-draw passed the godray depth test → godrays rendered
    // over what should have been the dim safe-zone area, brightening
    // worst-case bg L from 0.18 → 0.26 (+46%) and failing H1 line 1
    // cream at 2.89:1 < 3.0 threshold.
    //
    // Re-enabling depthWrite re-occludes godrays via water depth (≈7.0)
    // vs godray depth (≈14). Koi (camera-distance ≈6.5) are CLOSER
    // than water (≈7.0), so they draw in opaque bucket first + water's
    // depth test fails over koi pixels — koi remain visible below the
    // transparent zone, just without alpha-blended water tinting.
    //
    // Architectural trade: koi appear sharp (no water tint) vs Path B's
    // original "translucent water over koi" effect. Safe-zone constraint
    // remains first-class scene property (zone-graduated alpha still in
    // effect for transparent zone above the floor edge).
    depthWrite: true,
  });
  return { material, uniforms };
}
